import { FUND_ID_TN_STR, FUND_STATUS_NOT_DEPLOYED, INVESTUNIT_ID_TN_STR } from '@/src/utils/constants';
import { type PaymentKeyHash, type Script } from '@lucid-evolution/lucid';
import BigNumber from 'bignumber.js';
import 'reflect-metadata';
import {
    AssetClass,
    asSmartDBEntity,
    Convertible,
    getNumberx1e6,
    isMainnet,
    isNullOrBlank,
    Maybe,
    OptionsGet,
    optionsGetMinimal,
    optionsGetMinimalWithCallBack,
    setAndLoosePrecision1e6GetOnlyNumerator,
    strToHex,
    truncateBigNumber,
    type CS,
    type POSIXTime,
} from '../Commons';
import { FundHoldingEntity } from './Fund.Holding.Entity';
import { BaseSmartDBEntity, SmartUTxOEntity } from './index.exports';
import { InvestUnitEntity } from './Invest.Unit.Entity';
import { InvestUnitWithDetailsEntity } from './Invest.Unit.WithDetails.Entity';

@asSmartDBEntity()
export class FundEntity extends BaseSmartDBEntity {
    protected static _apiRoute: string = 'funds';
    protected static _className: string = 'Fund';
    // protected static _mongoTableName: string = 'Fund';

    protected static _plutusDataIndex = 0;
    protected static _isOnlyDatum = false;

    // #region fields

    _NET_id_TN_Str: string = FUND_ID_TN_STR;

    _isDeployed = false;

    // sobre escribo este campo del base smart db, por que alli no se carga y para el fondo es necesario cargarlo para ver si el fondo esta deployado o no
    @Convertible({ type: String, propertyToFill: 'smartUTxO', relation: 'OneToOne', typeRelation: SmartUTxOEntity, cascadeSave: true, cascadeLoad: true })
    smartUTxO_id!: string | undefined;

    // @Convertible({ propertyToFill: "protocol", relation: "ManyToOne", typeRelation: (await import('./protocol')).Protocol })
    //TODO: se hace una referencia ciruclar entre fund y protocol... tendria que ver como solucionarlo
    // protocol!: any | undefined;

    @Convertible({ isDB_id: true })
    protocol_id!: string;

    @Convertible()
    protocolVersion!: number;

    @Convertible({ propertyToFill: 'investUnit', relation: 'OneToOne', typeRelation: InvestUnitEntity, cascadeSave: false, cascadeLoad: false })
    investUnit_id!: string;
    investUnit!: InvestUnitEntity | undefined;

    @Convertible({ propertyToFill: 'fundHoldings', relation: 'OneToMany', typeRelation: FundHoldingEntity, cascadeSave: false, cascadeLoad: false })
    fundHoldings_ids!: string[];
    fundHoldings!: FundHoldingEntity[];

    @Convertible()
    hasFundHoldings: Boolean = false;

    @Convertible({ isUnique: true })
    name!: string;

    @Convertible()
    status: string = FUND_STATUS_NOT_DEPLOYED;

    @Convertible()
    total_FT_Minted!: bigint;

    @Convertible()
    tokenEmergencyAdmin_CS!: CS;

    @Convertible({ type: BigInt })
    priceADAx1e6!: bigint | undefined;

    @Convertible({ type: BigInt })
    TVLx1e6!: bigint | undefined;

    @Convertible()
    change24!: number | undefined;

    @Convertible({ type: BigInt })
    feeEarned!: bigint | undefined;

    @Convertible({ type: BigInt })
    staked!: bigint | undefined;

    @Convertible()
    showInSite: boolean = true;

    @Convertible()
    showInHome: boolean = false;

    @Convertible()
    isOfficial: boolean = false;

    @Convertible()
    fdpFundVersion!: number;

    @Convertible()
    fdpFundPolicy_Params!: object;

    @Convertible()
    fdpFundPolicy_Script!: Script;

    @Convertible({ isUnique: true })
    fdpFundPolicy_CS!: CS;

    @Convertible()
    fdpFundValidator_AddressMainnet!: string;

    @Convertible()
    fdpFundValidator_AddressTestnet!: string;

    @Convertible()
    fdpFundValidator_Script!: Script;

    @Convertible()
    fdpFundValidator_Hash!: string;

    @Convertible()
    fdpFundValidator_Params!: object;

    @Convertible()
    fdpFundHoldingPolicyID_Params!: object;

    @Convertible()
    fdpFundHoldingPolicyID_Script!: Script;

    @Convertible()
    fdpFundHoldingPolicyID_CS!: CS;

    @Convertible()
    fdpFundHoldingValidator_Params!: object;

    @Convertible()
    fdpFundHoldingValidator_Hash!: string;

    @Convertible()
    fdpFundHoldingValidator_Script!: Script;

    @Convertible()
    fdpFundHoldingValidator_AddressTestnet!: string;

    @Convertible()
    fdpFundHoldingValidator_AddressMainnet!: string;

    @Convertible({ isCreatedAt: true })
    createdAt!: Date;

    @Convertible({ isUpdatedAt: true })
    updatedAt!: Date;

    // #endregion fields

    // #region datum

    @Convertible({ isForDatum: true })
    fdFundVersion!: number;

    @Convertible({ isForDatum: true })
    fdFundPolicy_CS!: CS;

    @Convertible({ isForDatum: true })
    fdFundFT_TN_Hex!: string;

    @Convertible({ isForDatum: true })
    fdFundValidator_Hash!: string;

    @Convertible({ isForDatum: true })
    fdFundHoldingPolicyID_CS!: CS;

    @Convertible({ isForDatum: true })
    fdFundHoldingValidator_Hash!: string;

    @Convertible({ isForDatum: true })
    fdInvestUnitValidator_Hash!: string;

    @Convertible({ isForDatum: true, type: String })
    fdAdmins!: PaymentKeyHash[];

    @Convertible({ isForDatum: true, type: String })
    fdTokenAdminPolicy_CS!: CS;

    @Convertible({ isForDatum: true })
    fdFund_Category_Number!: number;

    @Convertible({ isForDatum: true, type: BigInt })
    fdBeginAt!: POSIXTime;

    @Convertible({ isForDatum: true, type: BigInt })
    fdDeadline!: POSIXTime;

    @Convertible({
        isForDatum: true,
        type: Maybe<POSIXTime>,
        fromPlainObject: Maybe.fromPlainObject_BigInt,
    })
    fdClosedAt!: Maybe<POSIXTime>;

    @Convertible({ isForDatum: true })
    fdCommission_PerYear_InBPx1e3!: bigint;

    @Convertible({ isForDatum: true, type: BigInt })
    fdCommissions_Table_Numerator_1e6!: [bigint];

    @Convertible({ isForDatum: true })
    fdHoldingsCount!: number;

    @Convertible({ isForDatum: true })
    fdHoldingsIndex!: number;

    @Convertible({ isForDatum: true })
    fdMaxDepositAndWithdraw!: bigint;

    @Convertible({ isForDatum: true, type: AssetClass })
    fdTokenGov_AC!: AssetClass;

    @Convertible({ isForDatum: true })
    fdRequiredTokenGov!: bigint;

    @Convertible({ isForDatum: true })
    fdMinADA!: bigint;

    // #endregion datum

    // #region db

    public static defaultFieldsWhenUndefined: Record<string, boolean> = {
        fdpFundPolicy_Script: false,
        fdpFundValidator_Script: false,
        fdpFundHoldingPolicyID_Script: false,
        fdpFundHoldingValidator_Script: false,
        fdpInvestUnitValidator_Script: false,
    };

    public static alwaysFieldsForSelect: Record<string, boolean> = {
        ...super.alwaysFieldsForSelect,
        protocol_id: true,
        investUnit_id: true,
        fundHoldings_ids: true,
        // fdFundPolicy_CS: true, aseguraeme que se usa siempre fdpFundPolicy_CS en lugar de fdFundPolicy_CS
        fdpFundPolicy_CS: true,
        fdFundFT_TN_Hex: true,
        name: true,
        hasFundHoldings: true,
        status: true,
        showInSite: true,
        showInHome: true,
        isOfficial: true,
        total_FT_Minted: true,
        fdCommissions_Table_Numerator_1e6: true, //TODO se usa solo en deposito creo
        fdpFundPolicy_Params: true, //TODO se usa en deploy
        fdpFundValidator_AddressMainnet: true,
        fdpFundValidator_AddressTestnet: true,
    };

    public static alwaysFieldsForCallbackOnAfterLoad: Record<string, boolean> = {
        ...super.alwaysFieldsForCallbackOnAfterLoad,
        fdBeginAt: true,
        fdDeadline: true,
        fdClosedAt: true,
    };

    public static defaultFieldsAddScriptsTxScript: Record<string, boolean> = {
        fdpFundPolicyPolicyID_CS: true,
        fdpFundPolicy_Script: true,
        fdpFundValidator_Hash: true,
        fdpFundValidator_Script: true,
        fdpFundHoldingPolicyID_CS: true,
        fdpFundHoldingPolicyID_Script: true,
        fdpFundHoldingValidator_Hash: true,
        fdpFundHoldingValidator_Script: true,
    };

    public static defaultFieldsForFundHoldingScripts: Record<string, boolean> = {
        fdpFundHoldingPolicyID_Params: true,
        fdpFundHoldingPolicyID_Script: true,
        fdpFundHoldingPolicyID_CS: true,
        fdpFundHoldingValidator_Params: true,
        fdpFundHoldingValidator_Hash: true,
        fdpFundHoldingValidator_Script: true,
        fdpFundHoldingValidator_AddressTestnet: true,
        fdpFundHoldingValidator_AddressMainnet: true,
    };

    //---------------------------

    public static optionsGetForFundList_With_Name_And_Tokens_SortedByTVL: OptionsGet = {
        ...optionsGetMinimal,
        fieldsForSelect: { name: true, fdFundFT_TN_Hex: true, fdpFundPolicy_CS: true, investUnit_id: true },
        loadRelations: { investUnit_id: true },
        optionsGetForRelation: { investUnit_id: { ...optionsGetMinimalWithCallBack, fieldsForSelect: { ...InvestUnitWithDetailsEntity.alwaysFieldsForSelect } } },
        doCallbackAfterLoad: true, //TODO esto no hace falta una vez que saque assetDetails del callbakonload y me base solamente en el token store
        sort: { TVLx1e6: -1 },
    };

    public static optionsGetForFundList_Only_Name_SortedByTVL: OptionsGet = {
        ...optionsGetMinimal,
        fieldsForSelect: { name: true, fdFundFT_TN_Hex: true, fdpFundPolicy_CS: true },
        sort: { TVLx1e6: -1 },
    };

    public static optionsGetForFundList_Only_Name_And_BasicTokens_SortedByTVL: OptionsGet = {
        ...optionsGetMinimal,
        fieldsForSelect: { name: true, fdFundFT_TN_Hex: true, fdpFundPolicy_CS: true, investUnit_id: true },
        loadRelations: { investUnit_id: true },
        optionsGetForRelation: { investUnit_id: { ...optionsGetMinimal,doCallbackAfterLoad: true, fieldsForSelect: { ...InvestUnitWithDetailsEntity.alwaysFieldsForSelect } } },
        doCallbackAfterLoad: true,
        sort: { TVLx1e6: -1 },
    };

    public static optionsGetFundList_ForFilterStatus: OptionsGet = {
        ...optionsGetMinimal,
        fieldsForSelect: { status: true },
    };

    public static optionsGetForFundList_ForPublic: OptionsGet = {
        ...optionsGetMinimal,
        fieldsForSelect: {
            fdpFundValidator_AddressMainnet: true,
            fdpFundValidator_AddressTestnet: true,
            fdCommission_PerYear_InBPx1e3: true,
            commissions_Table_Numerator_1e6: true,
            total_FT_Minted: true,
            priceADAx1e6: true,
            TVLx1e6: true,
            change24: true,
            feeEarned: true,
            staked: true,
            showInSite: true,
            showInHome: true,
            isOfficial: true,
            fdpFundPolicy_CS: true,
            fdpFundHoldingPolicyID_CS: true,
            fdFundFT_TN_Hex: true,
            fdAdmins: true,
            fdBeginAt: true,
            fdDeadline: true,
            fdClosedAt: true,
            fdMaxDepositAndWithdraw: true,
            fdTokenGov_AC: true,
            fdRequiredTokenGov: true,
        },
        loadRelations: { investUnit_id: true, fundHoldings_ids: true },
        optionsGetForRelation: {
            investUnit_id: { ...optionsGetMinimalWithCallBack, fieldsForSelect: { ...InvestUnitWithDetailsEntity.alwaysFieldsForSelect } },
            fundHoldings_ids: { ...optionsGetMinimal, fieldsForSelect: { ...FundHoldingEntity.alwaysFieldsForSelect } },
        },
        doCallbackAfterLoad: true,
        sort: { TVLx1e6: -1 },
    };

    public static optionsGetForFundList_ForAdmin: OptionsGet = {
        ...optionsGetMinimal,
        fieldsForSelect: {
            fdpFundValidator_AddressMainnet: true,
            fdpFundValidator_AddressTestnet: true,
            fdpFundHoldingValidator_AddressTestnet: true,
            fdpFundHoldingValidator_AddressMainnet: true,
            fdCommission_PerYear_InBPx1e3: true,
            fdFund_Category_Number: true,
            protocolVersion: true,
            fdpFundVersion: true,
            commissions_Table_Numerator_1e6: true,
            total_FT_Minted: true,
            priceADAx1e6: true,
            TVLx1e6: true,
            change24: true,
            feeEarned: true,
            staked: true,
            showInSite: true,
            showInHome: true,
            isOfficial: true,
            fdpFundPolicy_CS: true,
            fdpFundHoldingPolicyID_CS: true,
            fdTokenAdminPolicy_CS: true,
            fdFundFT_TN_Hex: true,
            fdAdmins: true,
            fdBeginAt: true,
            fdDeadline: true,
            fdClosedAt: true,
            fdMaxDepositAndWithdraw: true,
            fdTokenGov_AC: true,
            fdRequiredTokenGov: true,
            fdMinADA: true,
        },
        loadRelations: { investUnit_id: true, fundHoldings_ids: true, smartUTxO_id: true },
        optionsGetForRelation: {
            investUnit_id: { ...optionsGetMinimalWithCallBack, fieldsForSelect: { ...InvestUnitWithDetailsEntity.alwaysFieldsForSelect } },
            fundHoldings_ids: { ...optionsGetMinimal, fieldsForSelect: { ...FundHoldingEntity.alwaysFieldsForSelect } },
        },
        doCallbackAfterLoad: true,
        checkRelations: true,
        sort: { TVLx1e6: -1 },
    };

    // #endregion db

    // #region class methods

    public getNet_Address(): string {
        if (isMainnet) {
            return this.fdpFundValidator_AddressMainnet;
        } else {
            return this.fdpFundValidator_AddressTestnet;
        }
    }

    public getNET_id_CS(): string {
        return this.fdpFundPolicy_CS;
    }

    public getIinvestUnitID_AC_Lucid(): string {
        return this.getNET_id_CS() + strToHex(INVESTUNIT_ID_TN_STR);
    }

    public getNet_FundHoldingPolicyID_CS(): string {
        return this.fdpFundHoldingPolicyID_CS;
    }

    public getNet_FundHolding_Validator_Address(): string {
        if (isMainnet) {
            return this.fdpFundHoldingValidator_AddressMainnet;
        } else {
            return this.fdpFundHoldingValidator_AddressTestnet;
        }
    }

    public isAdmin(pkh: PaymentKeyHash): boolean {
        if (isNullOrBlank(pkh)) {
            throw `pkh not defined`;
        }
        return this.fdAdmins.includes(pkh);
    }

    // public static getRemainingDays(fundDatum_In: FundDatum, date: POSIXTime): number {
    //     const msPerDay = 86_400_000n; // 1000 * 60 * 60 * 24
    //     // const nowDate = new Date(parseInt(date.toString()))
    //     // const beginAt = new Date(parseInt(fundDatum_In.fdBeginAt.toString()))
    //     // const deadline = new Date(parseInt(fundDatum_In.fdDeadline.toString()))
    //     const daysRemaining = Math.floor(Number(fundDatum_In.fdDeadline / msPerDay)) - Math.floor(Number(date / msPerDay));
    //     return daysRemaining;
    // }

    public static getRemainingMonths(deadline: POSIXTime, date: POSIXTime): number {
        const msPerMonth = 2_592_000_000n; // -- 1000 * 60 * 60 * 24 * 30
        const msRemaining = deadline - date;
        if (msRemaining <= 0) {
            return 0;
        }
        const monthsRemaining_ = BigNumber(msRemaining.toString()).div(BigNumber(msPerMonth.toString()));
        const monthsRemaining = Number(truncateBigNumber(monthsRemaining_));
        return monthsRemaining;
    }

    public static getDepositComissions(commissions_Table_Numerator_1e6: bigint[], deadline: POSIXTime, date: POSIXTime): number {
        //------------------
        const monthsRemaining = this.getRemainingMonths(deadline, date);
        if (monthsRemaining < 0) {
            throw `Deadline must be greater than the current date.`;
        }
        //------------------
        const commissionsAcumulated_Numerator1e6 = commissions_Table_Numerator_1e6[monthsRemaining + 1];
        const commissionsAcumulated_Numerator1e6_ = Number(commissionsAcumulated_Numerator1e6.toString());
        const commissionsAcumulated_ = getNumberx1e6(commissionsAcumulated_Numerator1e6_)!;
        //------------------
        if (commissionsAcumulated_ <= 0) {
            throw `Invalid commission value in table.`;
        }
        //------------------
        //console.log(`[${this.className()}] - getDepositComissions - commissionsAcumulated_: ${showData(commissionsAcumulated_)}`);
        return commissionsAcumulated_;
    }

    public static calculatePriceFTBasedInDepositComissions(commissions_Table_Numerator_1e6: bigint[], deadline: POSIXTime, date: POSIXTime, price: bigint): { price: bigint } {
        //------------------
        const commissionsAcumulated_ = this.getDepositComissions(commissions_Table_Numerator_1e6, deadline, date);
        //------------------
        const price_ = Number(price.toString());
        //------------------
        const adjustedPrice = BigInt(Math.ceil(price_ / commissionsAcumulated_));
        //------------------
        //console.log(`[${this.className()}] - calculatePriceFTBasedInDepositComissions - adjustedPrice: ${showData(adjustedPrice)}`);
        return { price: adjustedPrice };
    }

    public static getCommissionsAvailable(deadline: POSIXTime, commission: bigint, ratePerMonth_Numerator1e6: bigint, shareBP1e2: bigint, taken: bigint, date: POSIXTime): bigint {
        const monthsRemainingRational = this.getRemainingMonths(deadline, date);
        const totalCommisionsAcum = commission;
        const rate = getNumberx1e6(ratePerMonth_Numerator1e6);
        let commisionsReady = Number(totalCommisionsAcum) - monthsRemainingRational * rate!;
        // if (commisionsReady < -1) {
        //     throw `commisionsReady < -1: ${commisionsReady}`;
        // }
        if (commisionsReady < 0) {
            commisionsReady = 0;
        }
        const shareDecimal = Number(shareBP1e2) / (10_000 * 100);
        const commisionsReady_share = commisionsReady * shareDecimal;
        const result = BigInt(Math.floor(commisionsReady_share)) - taken;
        console.log(
            `getCommissionsAvailable - commisionsReady: ${commisionsReady} - shareDecimal: ${shareDecimal} - commisionsReady_share: ${commisionsReady_share} - result: ${result}`
        );
        return result;
    }

    public static calculateDepositCommissions(
        commissions_Table_Numerator_1e6: bigint[],
        deadline: POSIXTime,
        date: POSIXTime,
        deposit: bigint
    ): { userFT: bigint; commissionsFT: bigint; commissions_Release_PerMonth_1e6: bigint } {
        //------------------
        // -- las comisiones son por año y en basic points bp multiplicados por 1e3
        // -- 1e3 para pasarlo a bp
        // -- 100 para pasarlo a porcentaje normal del 1 al 100
        // -- 100 para pasarlo a porcentaje del 0 al 1
        // -- 12 para pasarlo a meses
        // -- defino den = 1e3 * 100 * 100 * 12 = 1000 * 100 * 100 * 12 = 120_000_000
        // -- la formula de comisiones es = (1 - comisiones por periodo en porcentaje del 0 al 1) ^ (periodos restantes + 1)
        // Estoy usando una tabla de comisiones pre calculada con esa formula
        // la tabla tiene en cada posicion el valor de las comisiones acumuladas para ese cantidad de mes restantes
        // ademas esta guardada como enteros x 1e6, por lo que para obtener el valor real hay que dividir por 1e6
        //------------------
        // //console.log(`[${this.className()}] - calculateDepositCommissions - deposit: ${log(deposit)}`);
        //------------------
        const monthsRemaining = this.getRemainingMonths(deadline, date);
        if (monthsRemaining < 0) {
            throw `Deadline must be greater than the current date.`;
        }
        const monthsRemaining_ = Number(monthsRemaining);
        //------------------
        const deposit_ = Number(deposit.toString());
        //------------------
        const commissionsAcumulated_Numerator1e6 = commissions_Table_Numerator_1e6[monthsRemaining + 1];
        const commissionsAcumulated_Numerator1e6_ = Number(commissionsAcumulated_Numerator1e6.toString());
        const commissionsAcumulated_ = getNumberx1e6(commissionsAcumulated_Numerator1e6_)!;
        //------------------
        //console.log(`[${this.className()}] - calculateDepositCommissions - deposit: ${showData(deposit)}`);
        //------------------
        const userFT = BigInt(Math.floor(commissionsAcumulated_ * deposit_));
        //console.log(`[${this.className()}] - calculateDepositCommissions - userFT: ${showData(userFT)}`);
        //------------------
        if (userFT === 0n) {
            return { userFT, commissionsFT: 0n, commissions_Release_PerMonth_1e6: 0n };
        }
        //------------------
        const commissionsFT = deposit - userFT;
        const commissionsFT_ = Number(commissionsFT.toString());
        //console.log(`[${this.className()}] - calculateDepositCommissions - commissionsFT: ${showData(commissionsFT)}`);
        //------------------
        const commissionsRatePerMonth = monthsRemaining === 0 ? new BigNumber(0) : new BigNumber(commissionsFT_).dividedBy(new BigNumber(monthsRemaining_));
        const commissions_Release_PerMonth_1e6 = setAndLoosePrecision1e6GetOnlyNumerator(commissionsRatePerMonth);
        //console.log(`[${this.className()}] - calculateDepositCommissions - commissions_Release_PerMonth_1e6: ${showData(commissions_Release_PerMonth_1e6)}`);
        return { userFT, commissionsFT, commissions_Release_PerMonth_1e6 };
    }

    public static calculateDepositCommissions_FromUserFT(
        commissions_Table_Numerator_1e6: bigint[],
        deadline: POSIXTime,
        date: POSIXTime,
        userFT: bigint
    ): { deposit: bigint; commissionsFT: bigint } {
        //------------------
        const monthsRemaining = this.getRemainingMonths(deadline, date);
        if (monthsRemaining < 0) {
            throw `Deadline must be greater than the current date.`;
        }
        //------------------
        const commissionsAcumulatedNotIncludingThisPeriod_Numerator1e6 = commissions_Table_Numerator_1e6[monthsRemaining + 1];
        const commissionsAcumulatedNotIncludingThisPeriod_Numerator1e6_ = Number(commissionsAcumulatedNotIncludingThisPeriod_Numerator1e6.toString());
        const commissionsAcumulatedNotIncludingThisPeriod_ = getNumberx1e6(commissionsAcumulatedNotIncludingThisPeriod_Numerator1e6_)!;
        //------------------
        //console.log(`[${this.className()}] - calculateDepositCommissions_FromUserFT - userFT: ${showData(userFT)}`);
        //------------------
        // We need to deduce userFT and commissionsFT
        const userFT_ = Number(userFT.toString());
        //------------------
        // Deduce the value of userFT using the rate
        // userFT = rate * deposit;
        // deposit = userFT / rate;
        // commissionsFT = deposit - userFT;
        //------------------
        const deposit_ = userFT_ / commissionsAcumulatedNotIncludingThisPeriod_;
        const deposit = BigInt(Math.ceil(deposit_)); // Truncate or round as necessary
        //console.log(`[${this.className()}] - calculateDepositCommissions_FromUserFT - deposit: ${showData(deposit)}`);
        //------------------
        const commissionsFT = deposit - userFT; // Truncate or round as necessary
        //console.log(`[${this.className()}] - calculateDepositCommissions_FromUserFT - commissionsFT: ${showData(commissionsFT)}`);
        //------------------
        return { deposit, commissionsFT };
    }

    public static calculateWithdrawCommissions(
        commissions_Table_Numerator_1e6: bigint[],
        deadline: POSIXTime,
        date: POSIXTime,
        withdraw: bigint
    ): { commissionsForUserFTToGetBack: bigint; withdrawPlusCommissionsGetBack: bigint; commissions_Release_PerMonth_1e6: bigint } {
        // ------------------
        const monthsRemaining = this.getRemainingMonths(deadline, date);
        if (monthsRemaining < 0) {
            throw `Deadline must be greater than the current date.`;
        }
        const monthsRemaining_ = Number(monthsRemaining);
        //------------------
        const withdraw_ = Number(withdraw.toString());
        //------------------
        //console.log(`[${this.className()}] - calculateWithdrawCommissions - withdraw_: ${showData(withdraw_)}`);
        //------------------
        const commissionsAcumulatedNotIncludingThisPeriod_Numerator1e6 = commissions_Table_Numerator_1e6[monthsRemaining];
        const commissionsAcumulatedNotIncludingThisPeriod_Numerator1e6_ = Number(commissionsAcumulatedNotIncludingThisPeriod_Numerator1e6.toString());
        const commissionsAcumulatedNotIncludingThisPeriod_ = getNumberx1e6(commissionsAcumulatedNotIncludingThisPeriod_Numerator1e6_)!;
        //------------------
        const userFTforCalculationsOfCommissionsToGetBack = BigInt(Math.floor(commissionsAcumulatedNotIncludingThisPeriod_ * withdraw_));
        //------------------
        //console.log(`[${this.className()}] - calculateWithdrawCommissions - userFT: ${showData(userFTforCalculationsOfCommissionsToGetBack)}`);
        //------------------
        const commissionsForUserFTToGetBack = withdraw - userFTforCalculationsOfCommissionsToGetBack;
        const commissionsForUserFTToGetBack_ = Number(commissionsForUserFTToGetBack.toString());
        //------------------
        //console.log(`[${this.className()}] - calculateWithdrawCommissions - commissionsForUserFTToGetBack: ${showData(commissionsForUserFTToGetBack)}`);
        //------------------
        const withdrawPlusCommissionsGetBack = withdraw + commissionsForUserFTToGetBack;
        //------------------
        //console.log(`[${this.className()}] - calculateWithdrawCommissions - withdrawPlusCommissionsGetBack: ${showData(withdrawPlusCommissionsGetBack)}`);
        //------------------
        const commissionsRatePerMonth_ = monthsRemaining === 0 ? new BigNumber(0) : new BigNumber(commissionsForUserFTToGetBack_).dividedBy(new BigNumber(monthsRemaining_));
        const commissions_Release_PerMonth_1e6 = setAndLoosePrecision1e6GetOnlyNumerator(new BigNumber(commissionsRatePerMonth_));
        //------------------
        //console.log(`[${this.className()}] - calculateWithdrawCommissions - commissions_Release_PerMonth_1e6: ${showData(commissions_Release_PerMonth_1e6)}`);
        //------------------
        return { commissionsForUserFTToGetBack, withdrawPlusCommissionsGetBack, commissions_Release_PerMonth_1e6 };
    }

    public static calculateWithdrawCommissions_fromWithdrawPlusCommission(
        commissions_Table_Numerator_1e6: bigint[],
        deadline: POSIXTime,
        date: POSIXTime,
        withdrawPlusCommissions: bigint
    ): { commissionsForUserFTToGetBack: bigint; withdraw: bigint } {
        //------------------
        const monthsRemaining = this.getRemainingMonths(deadline, date);
        if (monthsRemaining < 0) {
            throw `Deadline must be greater than the current date.`;
        }
        //------------------
        const commissionsAcumulatedNotIncludingThisPeriod_Numerator1e6 = commissions_Table_Numerator_1e6[monthsRemaining];
        const commissionsAcumulatedNotIncludingThisPeriod_Numerator1e6_ = Number(commissionsAcumulatedNotIncludingThisPeriod_Numerator1e6.toString());
        const commissionsAcumulatedNotIncludingThisPeriod_ = getNumberx1e6(commissionsAcumulatedNotIncludingThisPeriod_Numerator1e6_)!;
        //------------------
        // Ensure the withdrawPlusCommissions value is in a usable format
        const withdrawPlusCommissions_ = Number(withdrawPlusCommissions.toString());
        //------------------
        //console.log(`[${this.className()}] - calculateWithdrawCommissions_fromWithdrawPlusCommission - withdrawPlusCommissions_: ${showData(withdrawPlusCommissions_)}`);
        //------------------
        // Deduce the value of withdraw using the formula:
        // withdrawPlusCommissions = withdraw * (2 - rate)
        const factor_1e6 = 2000000n - commissionsAcumulatedNotIncludingThisPeriod_Numerator1e6;
        const factor = getNumberx1e6(factor_1e6);
        if (factor === undefined || factor <= 0) {
            throw `Invalid commission factor: ${factor}`;
        }
        // Perform division and explicitly round down (truncate)
        const withdraw_ = new BigNumber(withdrawPlusCommissions_).dividedBy(factor);
        const withdraw = truncateBigNumber(withdraw_); // Explicitly round down for consistency
        //console.log(`[${this.className()}] - calculateWithdrawCommissions_fromWithdrawPlusCommission - withdraw: ${withdraw.toString()}, withdraw_: ${withdraw_}`);
        // Calculate the userFT for the given withdraw
        const userFTforCalculationsOfCommissionsToGetBack_ = Number(withdraw) * commissionsAcumulatedNotIncludingThisPeriod_;
        const userFTforCalculationsOfCommissionsToGetBack = BigInt(Math.floor(userFTforCalculationsOfCommissionsToGetBack_)); // Explicitly round down
        //console.log(`[${this.className()}] - calculateWithdrawCommissions_fromWithdrawPlusCommission - userFTforCalculationsOfCommissionsToGetBack_: ${userFTforCalculationsOfCommissionsToGetBack_}`);
        // Calculate the commissions for the userFT
        const commissionsForUserFTToGetBack = withdraw - userFTforCalculationsOfCommissionsToGetBack;
        //console.log(`[${this.className()}] - calculateWithdrawCommissions_fromWithdrawPlusCommission - commissionsForUserFTToGetBack: ${commissionsForUserFTToGetBack}`);
        return { commissionsForUserFTToGetBack, withdraw };
    }

    // #endregion class methods
}
